import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCase } from "../../provider/CaseProvider";
import { useAiChat } from "../../provider/AiChatProvider";
import { useDrawer } from "../../provider/DrawerProvider";
import { useAiEventStreamRegistry } from "../../provider/AiEventStreamProvider";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import { createResource } from "../../services/ReactQuery/reactQueryService";
import { sendAiChatPrompt, transcribeAiChatVoiceRecording } from "../../services/aiChatService";
import { canCancelVoiceRecording, insertTranscriptIntoText } from "../../services/aiChatVoiceInputService";
import { ResponseError } from "../../services/ApiClient";
import { SavedTextInputState, VoiceRecordingStatus } from "../../types/AIChat/AiChatVoiceInput";
import { VoiceRecorderFailure } from "../../types/VoiceRecorder";

type VoiceFinishMode = "insert" | "send";

type VoiceWorkflowStatus = "idle" | "transcribing" | "sending";

const RECORDER_ERROR_MESSAGES: Record<VoiceRecorderFailure, string> = {
  microphone: "Das Mikrofon konnte nicht gestartet werden. Bitte prüfe Berechtigung und Anschluss.",
  interrupted: "Die Verbindung zum Mikrofon wurde unterbrochen.",
  recording: "Die Aufnahme konnte nicht verarbeitet werden.",
};

const getTranscriptionErrorMessage = (error: unknown): string => {
  if (error instanceof ResponseError && error.message) {
    return error.message;
  }

  return "Die Transkription ist fehlgeschlagen.";
};

// One voice workflow from recording start to inserted/sent transcript. Lives in a ref, not in
// state: nothing renders it, and the async steps below mutate and read it directly. A session
// that was cancelled or unmounted is flagged discarded, which drops its late results.
type VoiceSession = {
  snapshot: SavedTextInputState;
  finishMode: VoiceFinishMode | null;
  discarded: boolean;
};

type UseAiChatVoiceInputOptions = {
  prompt: string;
  setPrompt: Dispatch<SetStateAction<string>>;
  inputRef: RefObject<HTMLTextAreaElement | null>;
};

export const useAiChatVoiceInput = ({ prompt, setPrompt, inputRef }: UseAiChatVoiceInputOptions) => {
  const [voiceWorkflowStatus, setVoiceWorkflowStatus] = useState<VoiceWorkflowStatus>("idle");
  const voiceSessionRef = useRef<VoiceSession | null>(null);
  const { product } = useCase();
  const { activeThreadId, setActiveThreadId, setIsCreatingThread, activeAgent, activeEditStepId } = useAiChat();
  const { flushDraftSave } = useDrawer();
  const { getStore: getStreamStore } = useAiEventStreamRegistry();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
  });

  const endVoiceSession = () => {
    setVoiceWorkflowStatus("idle");
    voiceSessionRef.current = null;
  };

  const resetVoiceInput = () => {
    if (voiceSessionRef.current) {
      voiceSessionRef.current.discarded = true;
    }

    recorder.cancel();
    voiceSessionRef.current = null;
    setVoiceWorkflowStatus("idle");
  };

  const restoreSelection = (saved: SavedTextInputState | undefined) => {
    if (!saved) {
      return;
    }
    setPrompt(saved.text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(saved.selectionStart, saved.selectionEnd);
    });
  };

  const restorePromptWithTranscript = (saved: SavedTextInputState, transcript: string) => {
    const { text, cursorPosition } = insertTranscriptIntoText(saved, transcript);
    setPrompt(text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const sendTranscript = async (session: VoiceSession, transcript: string) => {
    if (!product) {
      endVoiceSession();
      return;
    }
    try {
      if (activeEditStepId !== null) {
        await flushDraftSave();
        if (session.discarded) {
          return;
        }
      }
      await sendAiChatPrompt({
        product,
        activeThreadId,
        setActiveThreadId,
        createMutation,
        queryClient,
        getStreamStore,
        prompt: transcript,
        setIsCreatingThread,
        agentType: activeAgent,
        activeExecutionStepId: activeEditStepId,
      });
    } catch {
      restorePromptWithTranscript(session.snapshot, transcript);
      enqueueSnackbar("Die Nachricht konnte nicht gesendet werden.", { variant: "error" });
    }
    endVoiceSession();
  };

  const transcribe = async (session: VoiceSession, audio: Blob, mode: VoiceFinishMode) => {
    let transcript: string;
    try {
      transcript = (await transcribeAiChatVoiceRecording(audio)).transcript.trim();
    } catch (error) {
      if (session.discarded) {
        return;
      }
      endVoiceSession();
      restoreSelection(session.snapshot);
      enqueueSnackbar(getTranscriptionErrorMessage(error), { variant: "error" });
      return;
    }
    if (session.discarded) {
      return;
    }
    if (transcript === "") {
      endVoiceSession();
      restoreSelection(session.snapshot);
      enqueueSnackbar("Es wurde keine Sprache erkannt.", { variant: "error" });
      return;
    }
    if (mode === "send" && session.snapshot.text === "") {
      setVoiceWorkflowStatus("sending");
      sendTranscript(session, transcript);
      return;
    }
    restorePromptWithTranscript(session.snapshot, transcript);
    endVoiceSession();
  };

  // The recorder invokes these with the latest render's closures (see useVoiceRecorder), so the
  // results are handled right here: the session travels along as argument and a discarded one
  // drops its late results.
  const recorder = useVoiceRecorder({
    onRecorded: (audio) => {
      const session = voiceSessionRef.current;
      if (!session || session.discarded) {
        return;
      }
      // finishMode is set before every requested stop; the fallback only satisfies the type.
      const mode = session.finishMode ?? "insert";
      session.finishMode = null;
      setVoiceWorkflowStatus("transcribing");
      transcribe(session, audio, mode);
    },
    onFailed: (reason) => {
      restoreSelection(voiceSessionRef.current?.snapshot);
      voiceSessionRef.current = null;
      enqueueSnackbar(RECORDER_ERROR_MESSAGES[reason], { variant: "error" });
    },
  });

  const recorderStatus: VoiceRecordingStatus = recorder.status === "finishing" ? "transcribing" : recorder.status;
  const status: VoiceRecordingStatus = voiceWorkflowStatus === "idle" ? recorderStatus : voiceWorkflowStatus;

  const discardVoiceSession = () => {
    if (voiceWorkflowStatus === "sending") {
      // The send commit point is not cancellable.
      return;
    }
    if (voiceSessionRef.current) {
      voiceSessionRef.current.discarded = true;
    }
    recorder.cancel();
    endVoiceSession();
  };

  // Unmount-bound lifecycle: switching threads remounts the keyed chat input (AiChatProvider),
  // which must discard a running voice workflow — without this, a pending transcription would
  // still insert or send its transcript afterwards.
  useEffect(() => {
    return () => {
      if (voiceSessionRef.current) {
        voiceSessionRef.current.discarded = true;
      }
    };
  }, []);

  const startVoiceRecording = () => {
    if (!product || status !== "idle") {
      return;
    }

    const input = inputRef.current;
    // Snapshot text and cursor position before recording starts: the input unmounts while the
    // recording bar is shown, so the selection must be captured now to insert the transcript
    // at exactly this position later.
    voiceSessionRef.current = {
      snapshot: {
        text: prompt,
        selectionStart: input?.selectionStart ?? prompt.length,
        selectionEnd: input?.selectionEnd ?? prompt.length,
      },
      finishMode: null,
      discarded: false,
    };
    recorder.start();
  };

  const cancelVoiceRecording = () => {
    if (!canCancelVoiceRecording(status)) {
      return;
    }
    const snapshot = voiceSessionRef.current?.snapshot;
    discardVoiceSession();
    restoreSelection(snapshot);
  };

  const stopVoiceRecording = (mode: VoiceFinishMode) => {
    const session = voiceSessionRef.current;
    if (status !== "recording" || !session) {
      return;
    }
    session.finishMode = mode;
    recorder.stop();
  };

  return {
    status,
    waveform: recorder.waveform,
    resetVoiceInput,
    startVoiceRecording,
    cancelVoiceRecording,
    stopAndInsertVoiceRecording: () => stopVoiceRecording("insert"),
    finishVoiceRecording: () => stopVoiceRecording(voiceSessionRef.current?.snapshot.text === "" ? "send" : "insert"),
  };
};
